import * as formik from "formik";
import * as yup from "yup";

import { Col, Form, Row } from "react-bootstrap";
import { formatDateInEN, isNullOrEmpty } from "../../../../commons/util/HallAppUtil";
import { useMemo, useState } from "react";

import AppContainer from "../../../../commons/containers/AppContainer";
import AppInput from "../../../../commons/inputs/AppInput";
import AppLinkButton from "../../../../commons/buttons/AppLinkButton";
import CalendarInput from "../../../../commons/inputs/CalendarInput";
import HallAppToast from "../../../../commons/HallAppToast";

function PromotionStep1({ nextStep, initialData }) {
  const { Formik } = formik;

  // CalendarInput trabalha bem com Date
  const [startDate, setStartDate] = useState(
    initialData?.startDate ? new Date(initialData.startDate) : new Date()
  );
  const [endDate, setEndDate] = useState(
    initialData?.endDate ? new Date(initialData.endDate) : null
  );

  const [noEndDate, setNoEndDate] = useState(Boolean(initialData?.noEndDate));
  const [active, setActive] = useState(initialData?.active ?? true);

  const initialValues = useMemo(
    () => ({
      name: initialData?.name || "",
      description: initialData?.description || "",
    }),
    [initialData]
  );

  const schema = yup.object().shape({
    name: yup.string().required("Nome é obrigatório"),
    description: yup.string().max(140, "Máx. 140 caracteres"),
  });

  function handleStartDate(date) {
    // date pode vir string/Date dependendo do CalendarInput
    setStartDate(date ? new Date(date) : null);
  }

  function handleEndDate(date) {
    setEndDate(date ? new Date(date) : null);
  }

  const toNext = (values) => {
    if (isNullOrEmpty(values?.name)) {
      HallAppToast.error("Informe o nome da promoção.");
      return;
    }

    // Se não tem data final, ignora o endDate
    const start = startDate ? new Date(startDate) : null;
    const end = !noEndDate && endDate ? new Date(endDate) : null;

    // Valida fim >= início (quando ambos existem)
    if (start && end && end.getTime() < start.getTime()) {
      HallAppToast.error("A data final não pode ser menor que a data inicial.");
      return;
    }

    const payload = {
      name: values.name.trim(),
      description: (values.description || "").trim(),
      active,
      noEndDate,
      // Salva em YYYY-MM-DD (padrão que você já usa)
      startDate: start ? formatDateInEN(start) : null,
      endDate: end ? formatDateInEN(end) : null,
    };

    nextStep(payload);
  };

  return (
    <div>
      <Formik validationSchema={schema} initialValues={initialValues} enableReinitialize>
        {({ handleChange, values, touched, errors }) => (
          <Form>
            {/* Cabeçalho */}
            <div className="mb-3">
              <div style={{ fontSize: 16, fontWeight: 700, lineHeight: "20px" }}>
                Informações da promoção
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Defina nome, validade e status.
              </div>
            </div>

            {/* Seção: Nome e descrição */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #eee",
                borderRadius: 16,
                padding: 14,
                marginBottom: 12,
              }}
            >
              <Row className="g-2">
                <Col xs={12}>
                  <AppInput
                    label="Nome da promoção"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    isRequired
                    placeholder="Ex: Combo Escova + Hidratação"
                  />
                </Col>

                <Col xs={12}>
                  <AppInput
                    label="Descrição (opcional)"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    placeholder="Ex: Somente essa semana. Vagas limitadas."
                  />
                  <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>
                    {String(values.description || "").length}/140
                  </div>
                </Col>
              </Row>
            </div>

            {/* Seção: Validade (CalendarInput) */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #eee",
                borderRadius: 16,
                padding: 14,
                marginBottom: 12,
              }}
            >
              <div className="mb-2" style={{ fontSize: 14, fontWeight: 700 }}>
                Validade (opcional)
              </div>
              <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                Escolha a data de início e, se quiser, a data final.
              </div>

              {/* Data início */}
              <div style={{ marginBottom: 12 }}>
                <div className="mb-2" style={{ fontSize: 13, fontWeight: 700 }}>
                  Data inicial
                </div>

                <div className="commando-form-service-date">
                  <CalendarInput
                    selectionMode="single"
                    setDates={setStartDate}
                    changeDates={handleStartDate}
                    values={startDate || new Date()}
                  />
                </div>
              </div>

              {/* Data fim */}
              <div style={{ marginTop: 14 }}>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Data final</div>

                  <Form.Check
                    id="noEndDate"
                    type="switch"
                    label="Sem data final"
                    checked={noEndDate}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setNoEndDate(v);
                      if (v) setEndDate(null);
                    }}
                  />
                </div>

                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                  Se marcar “Sem data final”, a promoção fica válida até você desativar.
                </div>

                <div style={{ opacity: noEndDate ? 0.55 : 1, pointerEvents: noEndDate ? "none" : "auto" }}>
                  <div className="commando-form-service-date">
                    <CalendarInput
                      selectionMode="single"
                      setDates={setEndDate}
                      changeDates={handleEndDate}
                      values={endDate || new Date()}
                    />
                  </div>
                </div>

                {noEndDate && (
                  <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                    Sem data final: a promoção será válida até desativar.
                  </div>
                )}
              </div>
            </div>

            {/* Seção: Status */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #eee",
                borderRadius: 16,
                padding: 14,
                marginBottom: 12,
              }}
            >
              <Form.Check
                id="active"
                type="switch"
                label="Ativa"
                checked={Boolean(active)}
                onChange={(e) => setActive(e.target.checked)}
              />
              <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                Se desativar, ela não aparece para clientes e não fica em destaque.
              </div>
            </div>

            {/* Footer actions */}
            <div
              style={{
                position: "sticky",
                bottom: 0,
                padding: "10px 0",
                background: "linear-gradient(to top, #fff 70%, rgba(255,255,255,0))",
                borderTop: "1px solid #eee",
              }}
            >
              <AppContainer>
                <AppLinkButton name="Próximo" fullWidth onClick={() => toNext(values)} />
              </AppContainer>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default PromotionStep1;
